import React from "react";

import Topbar from "../../components/topbar";
import { useListSearch, ListTitle } from "../../components/ui/list-search";
import AddBankModal from "../../components/popup/add-bank";
import type { Column } from '../../components/admin-table';
import { Box } from "@mui/material";
import AddBtn from "../../components/ui/add-btn";
import DynamicAdminTable from "../../components/admin-table";
import Pagination from "../../components/pagination";
import DeletePopup from "../../components/popup/delete";
import { useToast } from "../../providers/toaster-provider";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";

const PAGE_SIZE = 10;

type Bank = {
    id: string;
    name: string;
    country: string;
    isOperational: boolean;
    isPresentational: boolean;
};

// What the search matches on - the bank's name and country.
const bankSearchText = (row: Record<string, any>) => [row.name, typeof row.country === 'string' ? row.country : row.country?.country];

const Bank: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const [data, setData] = React.useState<Record<string, any>[]>([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [selectedBank, setSelectedBank] = React.useState<any>(null);
    const [page, setPage] = React.useState(1);
    const list = useListSearch(data, bankSearchText);
    React.useEffect(() => { setPage(1); }, [list.term]);

    const [fetch, setFetch] = useApi();
    const [create, setCreate] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.bank.fetch, [{ param: 'includeAllActive', value: 'true' }]);
        setFetch({ url, method });
    }, [page, create, update, remove]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading) {
            setData(data?.map((bank: any) => ({
                id: bank.id,
                name: bank.name,
                country: bank.country,
                isOperational: bank.isOperational,
                isPresentational: bank.isPresentational,
                status: bank.status
            })));
        }
        if (error) {
            showError("Failed to fetch banks");
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = create;
        if (data && !isLoading) {
            showSuccess("Bank created successfully");
        }
        if (error) {
            showError("Failed to create bank");
        }
    }, [create]);

    React.useEffect(() => {
        const { data, isLoading, error } = update;
        if (data && !isLoading) {
            showSuccess("Bank updated successfully");
        }
        if (error) {
            showError("Failed to update bank");
        }
    }, [update]);

    React.useEffect(() => {
        const { data, isLoading, error } = remove;
        if (data && !isLoading) {
            showSuccess("Bank deleted successfully");
        }
        if (error) {
            showError("Failed to delete bank");
        }
    }, [remove]);

    const handleCreate = (bankData: any) => {
        const { url, method } = buildApiRequestConfig(routes.bank.create);
        setCreate({ url, method, body: bankData });
        setOpenModal('');
    }

    const handleUpdate = (bankData: any) => {
        const { url, method } = buildApiRequestConfig(routes.bank.update, [{ param: 'id', value: selectedBank.id }]);
        setUpdate({ url, method, body: bankData });
        setOpenModal('');
    }

    const handleDelete = (bankId: string) => {
        const { url, method } = buildApiRequestConfig(routes.bank.delete, [{ param: 'id', value: bankId }]);
        setRemove({ url, method });
    }

    const handleToggle = (row: any, columnId: string, value: boolean) => {
        const { url, method } = buildApiRequestConfig(columnId === 'isPresentational' ? routes.bank.togglePresentational : routes.bank.toggleOperational, [{ param: 'id', value: row.id }]);
        const body: any = {
            bankName: row.name,
            countryId: row.country,
        };
        body[columnId] = value;
        setUpdate({ url, method, body });
    }

    const columns: Column[] = [
        { id: 'name', label: ' Bank Name', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'country', label: 'Country', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { 
            id: 'isPresentational', 
            label: 'Presentation', 
            type: 'toggle', 
            size: 150, 
            onSort: (direction) => console.log(`Sort by amount ${direction}`), 
            onToggle: (row, value) => handleToggle(row, 'isPresentational', value)
        },
        { 
            id: 'isOperational', 
            label: 'Operational', 
            type: 'toggle', 
            size: 150, 
            onSort: (direction) => console.log(`Sort by amount ${direction}`), 
            onToggle: (row, value) => handleToggle(row, 'isOperational', value) },
        { 
            id: 'actions', 
            label: 'Actions', 
            size: 50, 
            type: 'actions', 
            onDelete: (row) => {
                setSelectedBank(row);
                setOpenModal('delete');
            },
            onEdit: (row) => {
                setSelectedBank(row);
                setOpenModal('edit');
            }
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddBankModal
                open={openModal === 'add' || openModal === 'edit'}
                onClose={() => setOpenModal('')}
                onConfirm={openModal === 'add' ? handleCreate : handleUpdate}
                isEdit={{ id: selectedBank?.id, state: openModal === 'edit' }}
            />
            <DeletePopup
                open={openModal === 'delete'}
                message="Delete Bank?"
                description="This action will permanently remove the selected bank."
                onCancel={() => setOpenModal('')}
                image={{ src: '', width: 0, height: 0 }}
                onConfirm={() => {
                    if(!selectedBank) return;
                    handleDelete(selectedBank.id);
                    setOpenModal('');
                }}
            />
            <Topbar title={<ListTitle title="Bank" list={list} placeholder="Search banks..." />} />
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="flex-end">
                    <AddBtn onClick={() => setOpenModal('add')} label="Add Bank" />
                </Box>
                <DynamicAdminTable 
                    columns={columns} 
                    data={list.filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)} 
                    pagination={
                        <Pagination
                            currentPage={page}
                            totalPages={Math.ceil(list.filtered.length / PAGE_SIZE)}
                            onPageChange={setPage}
                            pageSize={PAGE_SIZE}
                            totalItems={list.filtered.length}
                        />
                    }
                />
            </div>
        </div>
    );
};

export default Bank;